import {
  completedQuotationRequestState,
  matchRecommendationIdState,
} from "@/containers/MatchContainer/atoms";
import { matchRequestParlorLoadingState } from "@/atoms/match/matchRequestLoadingState";
import { Box } from "@chakra-ui/react";
import {
  colors,
  customEvent,
  Field,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  Spacing,
  VStack,
} from "goi_common";
import { useCallback, useLayoutEffect, useMemo, useReducer } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  장례식장_가격_시설_LIST,
  장례식장_선호_요인_VALUE_LIST,
} from "containers/MatchContainer/constants/request";
import { MatchRequestProps } from "@/containers/MatchContainer/type/requestType";
import { useDialog } from "@/hooks/useDialog";
import { postParlorRecommendationRequest } from "@/apis/sangjoQuotation";
import { useRouter } from "next/router";
import RequestUniqueFixedButton from "../RequestUniqueFixedButton";

function ParlorOnlyQuotationRequest() {
  const quotationRequest = useRecoilValue(completedQuotationRequestState);
  const matchRecommendationId = useRecoilValue(matchRecommendationIdState);

  const router = useRouter();
  useLayoutEffect(() => {
    if (!quotationRequest) {
      router.push("/match/request/");
    }
  }, []);

  const regionName = useMemo(() => {
    if (quotationRequest) {
      return `${FUNERAL_SIDO.get(Number(quotationRequest.funeral_sido)) as string} ${
        FUNERAL_SIGUNGU.get(Number(quotationRequest.funeral_sido))?.get(
          Number(quotationRequest.funeral_sigungu),
        ) as string
      }`;
    }
  }, [quotationRequest]);

  const { onOpen, onClose } = useDialog();

  const [SelectedOption, setSelectedOption] = useReducer(
    (prev: MatchRequestProps, next: any) => {
      return { ...prev, ...next };
    },
    {
      장례준비긴급도: "",
      장례식장_가격_시설: "",
      장례식장_선호_요인: "",
      sido: "",
      sigungu: "",
      조문객수: "",
    },
  );
  const isSubmitDisabled = useMemo(() => {
    return !SelectedOption.장례식장_가격_시설 || !SelectedOption.장례식장_선호_요인;
  }, [SelectedOption]);
  const handleSubmit = useCallback(async () => {
    if (loading) return;

    customEvent({
      newGtm: {
        path: "parlor_only_match_request",
        trigger_and_target: "submit_conversion",
      },
    });

    if (isSubmitDisabled) {
      onOpen({
        type: 1,
        closeOnOverlayClick: false,
        header: "응답하지 않은 항목이 있어요",
        children: <>다시 확인해주세요.</>,
        footerProps: {
          text: "확인",
          onClick: onClose,
        },
      });
      return;
    }

    try {
      setLoading(true);

      const body = {
        desired_type: SelectedOption.장례식장_가격_시설,
        most_important_factor: 장례식장_선호_요인_VALUE_LIST.find(
          (item) => item.text === SelectedOption.장례식장_선호_요인,
        )?.value as string,
      };

      const { status } = await postParlorRecommendationRequest({
        quotationRequestId: quotationRequest?.id as number,
        body,
      });
      if (status === 201) {
        router.replace(`/match/recommendation/${matchRecommendationId}/`);
      }
    } catch (e) {
      setLoading(false);
    }
  }, [SelectedOption]);

  const [loading, setLoading] = useRecoilState(matchRequestParlorLoadingState);

  return (
    <>
      <VStack padding="24px" width="100%">
        <Box
          width="100%"
          boxShadow="0px 0px 12px rgba(0, 0, 0, 0.12)"
          border={`1px solid ${colors.gray100}`}
          borderRadius="6px"
          background={colors.warmGray100}
          padding="16px"
          className="body3_r gray700"
        >
          상조 서비스 추천에서 응답하셨던 <br />
          {regionName} 근처의 장례식장을 추천해드려요.
          <br />
          이외 지역을 추천받고자 하시는 경우,
          <br />
          응답 후에 비교해볼 수 있어요.
        </Box>
        <Spacing gap={40} />

        <Field.Radio
          title="어떠한 장례식장을 찾고 계신가요?"
          items={장례식장_가격_시설_LIST}
          value={SelectedOption.장례식장_가격_시설}
          onChange={(e) => {
            setSelectedOption({ 장례식장_가격_시설: e });
            customEvent({
              newGtm: {
                path: "parlor_only_match_request",
                trigger_and_target: "parlor_price-answer",
                text: String(e),
              },
            });
          }}
          radioWidth="100%"
          flexDirection="column"
        />
        <Spacing gap={40} />
        <Field.Radio
          title="가장 중요하게 생각하시는 부분이 무엇인가요?"
          items={장례식장_선호_요인_VALUE_LIST.map((item) => item.text)}
          value={SelectedOption.장례식장_선호_요인}
          onChange={(e) => {
            setSelectedOption({ 장례식장_선호_요인: e });
            customEvent({
              newGtm: {
                path: "parlor_only_match_request",
                trigger_and_target: "parlor_factor-answer",
                text: String(e),
              },
            });
          }}
          radioWidth="100%"
          flexDirection="column"
        />
        <Spacing gap={108} />
      </VStack>

      <RequestUniqueFixedButton
        disabled={isSubmitDisabled}
        handleSubmit={handleSubmit}
        loading={loading}
      />
    </>
  );
}

export default ParlorOnlyQuotationRequest;
